import { gql } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

export const PATH_LEARNING_ASSET_RELATIONS = [
  "primary",
  "prerequisite",
  "supplement",
  "demonstration",
  "practice",
  "assessment",
  "review",
  "reference",
] as const;

export const PATH_LEARNING_ASSET_COVERAGE_ROLES = [
  "introduce",
  "explain",
  "demonstrate",
  "practice",
  "apply",
  "assess",
  "review",
  "reference",
] as const;

export type TeamCurriculumLearningAssetRelation =
  (typeof PATH_LEARNING_ASSET_RELATIONS)[number];
export type TeamCurriculumLearningAssetCoverageRole =
  (typeof PATH_LEARNING_ASSET_COVERAGE_ROLES)[number];
export type TeamCurriculumLearningAssetKind = "learn_course" | "learn_lesson";

export interface LearningAssetCourse {
  id: string;
  slug: string;
  title: string;
  thumbnailUrl: string | null;
  description: string;
  difficultyLevel: string;
  estimatedHours: number | null;
  lessonCount: number;
}

export interface LearningAssetLesson {
  id: string;
  slug: string;
  title: string;
  duration: number;
}

export interface TeamCurriculumLearningAsset {
  kind: TeamCurriculumLearningAssetKind;
  relationType: TeamCurriculumLearningAssetRelation;
  coverageRole: TeamCurriculumLearningAssetCoverageRole;
  required: boolean;
  displayOrder: number;
  course: LearningAssetCourse;
  lesson: LearningAssetLesson | null;
}

export interface TeamCurriculumLearningAssetIndexEntry {
  curriculumId: string;
  assets: TeamCurriculumLearningAsset[];
}

export interface LearnCoursePathItem {
  curriculumId: string;
  teamId: string;
  title: string;
  itemType: string;
  displayOrder: number;
  relationType: TeamCurriculumLearningAssetRelation;
  required: boolean;
}

const LEARNING_ASSET_FIELDS = gql`
  fragment TeamCurriculumLearningAssetFields on TeamCurriculumLearningAsset {
    kind
    relationType
    coverageRole
    required
    displayOrder
    course {
      id
      slug
      title
      thumbnailUrl
      description
      difficultyLevel
      estimatedHours
      lessonCount
    }
    lesson {
      id
      slug
      title
      duration
    }
  }
`;

export const TEAM_CURRICULUM_LEARNING_ASSETS_QUERY = gql`
  ${LEARNING_ASSET_FIELDS}
  query TeamCurriculumLearningAssets($curriculumId: String!) {
    teamCurriculumLearningAssets(curriculumId: $curriculumId) {
      ...TeamCurriculumLearningAssetFields
    }
  }
`;

export const TEAM_CURRICULUM_LEARNING_ASSET_INDEX_QUERY = gql`
  ${LEARNING_ASSET_FIELDS}
  query TeamCurriculumLearningAssetIndex(
    $teamId: String!
    $lpCourseId: String
  ) {
    teamCurriculumLearningAssetIndex(teamId: $teamId, lpCourseId: $lpCourseId) {
      curriculumId
      assets {
        ...TeamCurriculumLearningAssetFields
      }
    }
  }
`;

export const PATH_ITEMS_FOR_LEARN_COURSE_QUERY = gql`
  query PathItemsForLearnCourse($courseSlug: String!) {
    pathItemsForLearnCourse(courseSlug: $courseSlug) {
      curriculumId
      teamId
      title
      itemType
      displayOrder
      relationType
      required
    }
  }
`;

export async function getTeamCurriculumLearningAssets(
  curriculumId: string,
  options: GraphqlRequestConfig = {},
): Promise<TeamCurriculumLearningAsset[]> {
  const client = createGraphqlClient(options);
  const data = await client.request<{
    teamCurriculumLearningAssets: TeamCurriculumLearningAsset[];
  }>(TEAM_CURRICULUM_LEARNING_ASSETS_QUERY, { curriculumId });
  return data.teamCurriculumLearningAssets ?? [];
}

export async function getTeamCurriculumLearningAssetIndex(
  teamId: string,
  lpCourseId: string | null,
  options: GraphqlRequestConfig = {},
): Promise<TeamCurriculumLearningAssetIndexEntry[]> {
  const client = createGraphqlClient(options);
  const data = await client.request<{
    teamCurriculumLearningAssetIndex: TeamCurriculumLearningAssetIndexEntry[];
  }>(TEAM_CURRICULUM_LEARNING_ASSET_INDEX_QUERY, { teamId, lpCourseId });
  return data.teamCurriculumLearningAssetIndex ?? [];
}

export async function getPathItemsForLearnCourse(
  courseSlug: string,
  options: GraphqlRequestConfig = {},
): Promise<LearnCoursePathItem[]> {
  const client = createGraphqlClient(options);
  const data = await client.request<{
    pathItemsForLearnCourse: LearnCoursePathItem[];
  }>(PATH_ITEMS_FOR_LEARN_COURSE_QUERY, { courseSlug });
  return data.pathItemsForLearnCourse ?? [];
}
